/**
 * PortfolioTable — tabela de tenants do portfólio (admin, SP3).
 *
 * Exibe uma linha por `PortfolioTenantRow` com:
 *  - Tenant / conta AWS
 *  - Custo do período (formatMoney)
 *  - Budget (formatMoney) + % utilizado
 *  - Semáforo de status: Badge verde/amarelo/vermelho por `status`
 *  - MoM (formatPct {signed} + cor direcional: alta=red.6, queda=green.6)
 *  - Indicador de anomalia (ícone sutil na linha)
 *
 * Ordenação client-side por colunas numéricas: amount_usd, pct_used, mom_pct.
 * Clique no cabeçalho alterna asc/desc.
 *
 * Linha vazia → EmptyState. Cores apenas via tokens DS/Mantine.
 */
import { useState } from 'react'
import {
  Badge,
  Group,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconBuildingBank,
} from '@tabler/icons-react'

import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { formatMoney } from '@/components/finops/formatMoney'
import { formatPct } from '@/utils/finops'
import type { PortfolioTenantRow } from '@/api/finopsDashboard'

// ─── tipos e helpers ─────────────────────────────────────────────────────────

type SortKey = 'amount_usd' | 'pct_used' | 'mom_pct'
type SortDir = 'asc' | 'desc'

interface SortState {
  key: SortKey
  dir: SortDir
}

function statusColor(status: string): string {
  switch (status) {
    case 'verde':
      return 'green'
    case 'amarelo':
      return 'yellow'
    case 'vermelho':
      return 'red'
    default:
      return 'gray'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'verde':
      return 'OK'
    case 'amarelo':
      return 'Atenção'
    case 'vermelho':
      return 'Crítico'
    default:
      return status
  }
}

function momColor(mom: number | null): string {
  if (mom === null) return 'dimmed'
  return mom > 0 ? 'red.6' : 'green.6'
}

function PctArrow({ value }: { value: number | null }) {
  if (value === null || value === 0) return null
  return value > 0
    ? <IconArrowUp size={11} style={{ verticalAlign: 'middle' }} />
    : <IconArrowDown size={11} style={{ verticalAlign: 'middle' }} />
}

function sortRows(
  rows: PortfolioTenantRow[],
  { key, dir }: SortState,
): PortfolioTenantRow[] {
  return [...rows].sort((a, b) => {
    const valA = a[key] ?? -Infinity
    const valB = b[key] ?? -Infinity
    return dir === 'asc' ? valA - valB : valB - valA
  })
}

// ─── sub-componente: cabeçalho ordenável ─────────────────────────────────────

interface SortHeaderProps {
  label: string
  sortKey: SortKey
  current: SortState
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}

function SortHeader({ label, sortKey, current, onSort, align = 'right' }: SortHeaderProps) {
  const active = current.key === sortKey
  return (
    <Table.Th style={{ textAlign: align }}>
      <UnstyledButton
        onClick={() => onSort(sortKey)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
      >
        <Text size="xs" c={active ? 'inovareTeal.6' : 'dimmed'} fw={600}>
          {label}
        </Text>
        {active ? (
          current.dir === 'asc'
            ? <IconArrowUp size={11} color="var(--mantine-color-inovareTeal-6)" />
            : <IconArrowDown size={11} color="var(--mantine-color-inovareTeal-6)" />
        ) : (
          <IconArrowsUpDown size={11} color="var(--mantine-color-dimmed)" />
        )}
      </UnstyledButton>
    </Table.Th>
  )
}

// ─── componente principal ────────────────────────────────────────────────────

export interface PortfolioTableProps {
  rows: PortfolioTenantRow[]
  title?: string
}

export function PortfolioTable({
  rows,
  title = 'Portfólio — visão por organização',
}: PortfolioTableProps) {
  const [sort, setSort] = useState<SortState>({ key: 'amount_usd', dir: 'desc' })

  function handleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' },
    )
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={IconBuildingBank}
        title="Nenhuma organização no portfólio"
        description="Não há tenants com dados de custo disponíveis para o período selecionado."
        accent="primary"
        size="md"
      />
    )
  }

  const sorted = sortRows(rows, sort)

  return (
    <WidgetCard accent="none" p={0}>
      <CardHeader title={title} badge={{ value: rows.length, color: 'teal' }} />

      <Table
        striped
        highlightOnHover
        withColumnBorders={false}
        verticalSpacing={7}
        horizontalSpacing="md"
        fz="xs"
        style={{ tableLayout: 'fixed' }}
      >
        <Table.Thead>
          <Table.Tr>
            {/* Tenant / conta */}
            <Table.Th style={{ width: '28%' }}>
              <Text size="xs" c="dimmed" fw={600}>
                Tenant / Conta
              </Text>
            </Table.Th>

            {/* Status */}
            <Table.Th style={{ width: '10%', textAlign: 'center' }}>
              <Text size="xs" c="dimmed" fw={600}>
                Status
              </Text>
            </Table.Th>

            {/* Custo — ordenável */}
            <SortHeader
              label="Custo"
              sortKey="amount_usd"
              current={sort}
              onSort={handleSort}
            />

            {/* Budget % — ordenável */}
            <SortHeader
              label="Budget"
              sortKey="pct_used"
              current={sort}
              onSort={handleSort}
            />

            {/* MoM — ordenável */}
            <SortHeader
              label="MoM"
              sortKey="mom_pct"
              current={sort}
              onSort={handleSort}
            />
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {sorted.map((row) => {
            const color = momColor(row.mom_pct)
            const rowStyle = row.anomaly
              ? { background: 'rgba(245,156,26,0.06)' }
              : undefined

            return (
              <Table.Tr key={row.tenant_id} style={rowStyle}>
                {/* Tenant / conta */}
                <Table.Td>
                  <Group gap={4} wrap="nowrap" align="center">
                    {row.anomaly && (
                      <Tooltip
                        label="Variação atípica detectada"
                        position="top"
                        withArrow
                      >
                        <IconAlertTriangle
                          size={13}
                          color="var(--mantine-color-yellow-6)"
                          style={{ flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <Text size="xs" fw={500} lineClamp={1}>
                        {row.name}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {row.account_id || '—'}
                      </Text>
                    </div>
                  </Group>
                </Table.Td>

                {/* Status — semáforo */}
                <Table.Td style={{ textAlign: 'center' }}>
                  <Badge
                    size="xs"
                    color={statusColor(row.status)}
                    variant="light"
                  >
                    {statusLabel(row.status)}
                  </Badge>
                </Table.Td>

                {/* Custo */}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="xs" fw={600}>
                    {formatMoney(row.amount_usd)}
                  </Text>
                </Table.Td>

                {/* Budget */}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="xs">
                    {row.budget_usd > 0
                      ? `${row.pct_used.toFixed(0)}%`
                      : '—'}
                  </Text>
                  {row.budget_usd > 0 && (
                    <Text size="xs" c="dimmed">
                      {formatMoney(row.budget_usd)}
                    </Text>
                  )}
                </Table.Td>

                {/* MoM — null + amount>0 = tenant novo (sem histórico anterior) */}
                <Table.Td style={{ textAlign: 'right' }}>
                  {row.mom_pct === null && row.amount_usd > 0 ? (
                    <Badge size="xs" color="teal" variant="light">
                      novo
                    </Badge>
                  ) : (
                    <Group gap={2} justify="flex-end" wrap="nowrap" c={color}>
                      <PctArrow value={row.mom_pct} />
                      <Text size="xs" fw={700} c={color} span>
                        {formatPct(row.mom_pct, { signed: true })}
                      </Text>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </WidgetCard>
  )
}
